import { PointAddResponse } from "@1games-ru/region-shared/dist/client/requests/point";
import { PointType } from "@1games-ru/region-shared/dist/enums/point/point";
import { faker } from "@faker-js/faker";
import { invariant } from "motion/react";
import { Point } from "@/domains/createEntity/constants/point";

const defaultPoint = {
  position: null,
  dimension: null,
  heading: null,
  range: 1,
};

function getUniqueString(prefix: string, payload?: unknown): string {
  if (!payload || !(payload as { id: string }).id) {
    throw new Error("payload.id is required");
  }
  return `${prefix} ${(payload as { id: string }).id}`;
}

export default function getInitPoint(type: Point, payload?: unknown): PointAddResponse {
  switch (type) {
    case Point.HouseLocation: {
      return {
        groupName: "house",
        id: "entrance",
        type: PointType.Transit,
        content: "Точка дома",
        position: null,
        dimension: null,
        heading: null,
        range: 1,
      };
    }
    case Point.GarageLocation: {
      return {
        groupName: "house",
        id: "garage",
        type: PointType.Parking,
        content: "Точка гаража",
        position: null,
        dimension: null,
        heading: null,
        range: 3,
      };
    }
    case Point.GasStationLocation: {
      return {
        groupName: "gas",
        id: "area",
        type: PointType.Area,
        content: "Место заправки",
        position: null,
        dimension: null,
        heading: null,
        range: 30,
        ...(payload as { range: number }),
      };
    }
    case Point.GasStationViewLocation: {
      return {
        groupName: "gas",
        id: "sign",
        type: PointType.Place,
        content: "Место покупки заправки",
        position: null,
        dimension: null,
        heading: null,
        range: 1,
      };
    }
    case Point.ShopLocation: {
      return {
        groupName: "gas",
        id: "cashier",
        type: PointType.Place,
        content: "Место перед кассой",
        position: null,
        dimension: null,
        heading: null,
        range: 1,
      };
    }
    case Point.LoadLocation: {
      return {
        groupName: "gas",
        id: "warehouse",
        type: PointType.Place,
        content: "Место разгрузки топлива",
        position: null,
        dimension: null,
        heading: null,
        range: 1,
      };
    }
    case Point.GasStationColumnLocation: {
      const index = faker.number.int({ min: 1, max: 999 });
      return {
        groupName: "gas",
        id: `station-${index}`,
        type: PointType.Parking,
        content: `Место для заправки ${index}`,
        position: null,
        dimension: null,
        heading: null,
        range: 3,
      };
    }
    case Point.OfficeLocation: {
      return {
        groupName: "gas",
        id: `office`,
        type: PointType.Place,
        content: `Место дирэхтора`,
        position: null,
        dimension: null,
        heading: null,
        range: 1,
      };
    }
    case Point.CleaningLocation: {
      const index = faker.number.int({ min: 1, max: 999 });
      return {
        groupName: "gas",
        id: `cleaning-${index}`,
        type: PointType.Place,
        content: `Место для уборки ${index}`,
        position: null,
        dimension: null,
        heading: null,
        range: 1,
      };
    }

    case Point.Spawn: {
      return {
        groupName: "interior",
        id: "spawn",
        type: PointType.Area,
        content: getUniqueString("Точка спауна", payload),
        ...defaultPoint,
      };
    }
    case Point.InteriorLocation: {
      invariant(payload !== undefined, "Payload is required");

      return {
        groupName: "interior",
        id: getUniqueString("interior", payload),
        type: PointType.Area,
        content: getUniqueString("Точка интерьера", payload),
        ...defaultPoint,
        range: (payload as { range: number }).range,
      };
    }
    case Point.StreetTransit: {
      return {
        groupName: "garage",
        id: getUniqueString("street", payload),
        type: PointType.Transit,
        content: getUniqueString("Выход на улицу", payload),
        ...defaultPoint,
      };
    }
    case Point.GarageTransit: {
      return {
        groupName: "garage",
        id: getUniqueString("garage", payload),
        type: PointType.Transit,
        content: getUniqueString("Вход в гараж", payload),
        ...defaultPoint,
      };
    }
    case Point.LivingTransit: {
      return {
        groupName: "interior",
        id: getUniqueString("living", payload),
        type: PointType.Transit,
        content: getUniqueString("Вход в квартиру", payload),
        ...defaultPoint,
      };
    }
    case Point.Safe: {
      return {
        groupName: "interior",
        id: getUniqueString("safe", payload),
        type: PointType.Area,
        content: getUniqueString("Сейф", payload),
        ...defaultPoint,
      };
    }
    case Point.Wardrobe: {
      return {
        groupName: "interior",
        id: getUniqueString("wardrobe", payload),
        type: PointType.Area,
        content: getUniqueString("Гардероб", payload),
        ...defaultPoint,
      };
    }
    case Point.Kitchen: {
      return {
        groupName: "interior",
        id: getUniqueString("kitchen", payload),
        type: PointType.Area,
        content: getUniqueString("Кухня", payload),
        ...defaultPoint,
      };
    }
    case Point.Intercom: {
      return {
        groupName: "interior",
        id: getUniqueString("intercom", payload),
        type: PointType.Area,
        content: getUniqueString("Домофон", payload),
        ...defaultPoint,
      };
    }
    case Point.VehicleLocation: {
      const uuid = faker.string.uuid();

      return {
        groupName: "garage",
        id: `${getUniqueString("vehicle", payload)}-${uuid}`,
        type: PointType.Parking,
        content: getUniqueString("Парковочное место", payload),
        ...defaultPoint,
      };
    }
    case Point.ElevatorLocation: {
      const uuid = faker.string.uuid();
      return {
        groupName: "interior",
        id: `${getUniqueString("elevator", payload)}-${uuid}`,
        type: PointType.Transit,
        content: getUniqueString("Лифт", payload),
        ...defaultPoint,
      };
    }
    case Point.ExitLocation: {
      const uuid = faker.string.uuid();
      return {
        groupName: "interior",
        id: `${getUniqueString("exit", payload)}-${uuid}`,
        type: PointType.Transit,
        content: getUniqueString("Выход", payload),
        ...defaultPoint,
      };
    }
    case Point.RoomLocation: {
      const uuid = faker.string.uuid();
      return {
        groupName: "interior",
        id: `${getUniqueString("room", payload)}-${uuid}`,
        type: PointType.Area,
        content: getUniqueString("Комната", payload),
        ...defaultPoint,
      };
    }
    case Point.SignLocation: {
      const uuid = faker.string.uuid();
      return {
        groupName: "interior",
        id: `${getUniqueString("sign", payload)}-${uuid}`,
        type: PointType.Area,
        content: getUniqueString("Вывеска", payload),
        ...defaultPoint,
      };
    }

    default:
      break;
  }

  return {
    groupName: "house",
    id: "entrance",
    type: PointType.Place,
    content: "Точка дома",
    ...defaultPoint,
  };
}
